import React, { useRef, useEffect, useCallback } from 'react';
import { DualTitleHeaderComponent } from '@zawarski/eti-typescript-components';
import MenuIcon from '@mui/icons-material/Menu';
import { DashboardProps } from 'pages/Dashboard/Dashboard.props';
import { GridLayout } from 'components/layout';
import { ContactSupport, ModalAppWrapper, OnBoarding } from 'components/fragment';
import { AppAdminList } from '@/common/enum';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import WindowIcon from '@mui/icons-material/Window';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import { cn } from '@/lib/utils';
import useMediaQuery, { MOBILE_SIZE } from 'hooks/useMediaQuery';

const DashboardView: React.FC<DashboardProps> = ({
  addColumn,
  columnCount,
  isInitialOnBoarding,
  openIncident,
  openPositions,
  openControlPanel,
  openContactSupport,
  openOnBoarding,
  onCloseContactSupport,
  onCloseIncident,
  onClosePositions,
  onCloseControlPanel,
  onCloseOnBoarding,
  toggleDrawer,
  onBoardingSettings,
  programsList,
  programsHistory,
  notificationType,
  toggleNotificationType,
  progInFullScreen,
  username,
  groupName,
}) => {
  const { isMobileDevice, windowDimensions } = useMediaQuery();
  const isMobile =
    (windowDimensions && windowDimensions?.width ? windowDimensions.width < MOBILE_SIZE : false) ||
    isMobileDevice;
  const gridRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = React.useState(!!progInFullScreen);
  const hasAutoEnabledFullScreen = useRef(false);

  useEffect(() => {
    if (progInFullScreen !== undefined) {
      setIsFullScreen(!!progInFullScreen);
    }
  }, [progInFullScreen]);

  // Auto-enable fullscreen mode on mobile devices (only once per mobile session)
  useEffect(() => {
    if (isMobile && !isFullScreen && !hasAutoEnabledFullScreen.current && gridRef.current) {
      hasAutoEnabledFullScreen.current = true;
      const isOpen = gridRef.current.onProgramFullScreen();
      setIsFullScreen(isOpen);
    } else if (!isMobile) {
      // Reset the flag when returning to desktop so it can auto-enable again next time
      hasAutoEnabledFullScreen.current = false;
    }
  }, [isMobile, isFullScreen]);

  const openFullScreen = useCallback(() => {
    if (gridRef.current) {
      const isOpen = gridRef.current.onProgramFullScreen();
      setIsFullScreen(isOpen);
      // If user manually exits fullscreen on mobile, don't auto-re-enable
      if (!isOpen && isMobile) {
        hasAutoEnabledFullScreen.current = true;
      }
    }
  }, [isMobile]);

  return (
    <div className={'bg-white-50 flex h-full w-full flex-col'}>
      <DualTitleHeaderComponent
        titleBig={'Palmetto Dashboard'}
        titleSmall={`${username} - ${groupName}`}
        icon={
          <div onClick={toggleDrawer}>
            <MenuIcon sx={{ color: 'white' }} />
          </div>
        }
        suffix={
          <div className={'flex h-fit flex-row items-center justify-center gap-1'}>
            {!isMobile && !isFullScreen && (
              <>
                <div
                  className={cn(
                    'flex h-9 w-fit items-center justify-center text-nowrap text-white',
                    'p-2 font-semibold'
                  )}>
                  {columnCount} columns
                </div>
                <div
                  className={cn(
                    'flex h-9 w-fit items-center rounded-md px-4 py-2 font-semibold text-nowrap',
                    'cursor-pointer bg-white text-sm text-vulcan-500'
                  )}
                  onClick={addColumn}>
                  Add column
                </div>
              </>
            )}
            <div className='relative flex h-fit w-fit flex-row items-center justify-center rounded-md bg-[#0049ee] p-1'>
              {/* Sliding background */}
              <div
                className={cn(
                  'absolute top-1 left-1 h-8 w-8 rounded-md bg-white transition-transform duration-300 ease-in-out',
                  notificationType === 'marquee' ? 'translate-x-8' : 'translate-x-0'
                )}
              />

              {/* Toast button */}
              <div
                onClick={() => toggleNotificationType('toast')}
                className={cn(
                  'relative z-10 flex size-8 cursor-pointer items-center justify-center rounded-md p-2 transition-colors duration-300',
                  notificationType === 'toast' ? 'text-vulcan-500' : 'text-white'
                )}>
                <ChatBubbleIcon className={cn('size-4!')} />
              </div>

              {/* Marquee button */}
              <div
                onClick={() => toggleNotificationType('marquee')}
                className={cn(
                  'relative z-10 flex size-8 cursor-pointer items-center justify-center rounded-md p-2 transition-colors duration-300',
                  notificationType === 'marquee' ? 'text-vulcan-500' : 'text-white'
                )}>
                <CallToActionIcon className={cn('size-4!')} />
              </div>
            </div>
            <div className='cursor-pointer' onClick={openFullScreen}>
              <div className='relative flex h-fit w-fit flex-row items-center justify-center rounded-md bg-vulcan-500 p-1'>
                {/* Sliding background */}
                <div
                  className={cn(
                    'absolute top-1 left-1 h-8 w-8 rounded-md bg-[#e3e6ec] transition-transform duration-300 ease-in-out',
                    isFullScreen ? 'translate-x-8' : 'translate-x-0'
                  )}
                />

                {/* Grid view button */}
                <div
                  className={cn(
                    'relative z-10 flex size-8 cursor-pointer items-center justify-center rounded-md p-2 transition-colors duration-300',
                    !isFullScreen ? 'text-vulcan-500' : 'text-white'
                  )}>
                  <CalendarViewWeekIcon className={cn('size-4!')} />
                </div>

                {/* Full screen button */}
                <div
                  className={cn(
                    'relative z-10 flex size-8 cursor-pointer items-center justify-center rounded-md p-2 transition-colors duration-300',
                    isFullScreen ? 'text-vulcan-500' : 'text-white'
                  )}>
                  <WindowIcon className={cn('size-4!')} />
                </div>
              </div>
            </div>
          </div>
        }
      />
      <GridLayout
        ref={gridRef}
        onBoardingSettings={onBoardingSettings}
        programsHistory={programsHistory}
        programsList={programsList}
        notificationType={notificationType}
      />
      <OnBoarding
        isInitialOnBoarding={isInitialOnBoarding}
        open={openOnBoarding}
        onClose={onCloseOnBoarding}
        onBoardingSettings={onBoardingSettings}
        programsList={programsList}
        programsHistory={programsHistory}
      />
      <ContactSupport
        open={openContactSupport}
        onClose={onCloseContactSupport}
        programsList={programsList}
      />
      <ModalAppWrapper app={AppAdminList.Incidents} open={openIncident} onClose={onCloseIncident} />
      <ModalAppWrapper
        app={AppAdminList.Positions}
        open={openPositions}
        onClose={onClosePositions}
      />
      <ModalAppWrapper
        app={AppAdminList.ControlPanel}
        open={openControlPanel}
        onClose={onCloseControlPanel}
      />
    </div>
  );
};

export default DashboardView;
